import { ReactElement } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import ScrollToTop from './component/ScrollToTop';
import Login from './pages/Home';
import Auth from './pages/Login';
import Pricing from './pages/Pricing';
import Library from './pages/Library';
import Unsubscribe from './pages/unsubscribe';
import MyAccount from './pages/MyAccount';
import TermsAndConditions from './pages/Terms';
import PrivacyPolicy from './pages/Privacy';
import Checkout from './pages/Checkout';
import Imprint from './pages/Imprint';
import LiveClassesAdmin from './pages/LiveClassesAdmin';
import NotFound from './pages/NotFound';
import { SubscriptionRoute } from './components/SubscriptionRoute';
import { PricingRoute } from './components/PricingRoute';
import { AdminRoute } from './components/AdminRoute';
import { AdminRedirectRoute } from './components/AdminRedirectRoute';
import { useAuth } from './context/AuthContext';
import { TailSpin } from 'react-loader-spinner';

const AuthenticatedEntryRedirect = (): ReactElement => {
  const { isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F4F5F7]">
        <TailSpin color="rgb(17,58,40)" height={80} width={80} />
      </div>
    );
  }

  if (user?.is_admin) {
    return <Navigate to="/live-classes/" replace />;
  }

  return <Navigate to="/my-account/" replace />;
};

const PublicAuthRoute = ({ children }: { children: ReactElement }): ReactElement => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F4F5F7]">
        <TailSpin color="rgb(17,58,40)" height={80} width={80} />
      </div>
    );
  }

  return isAuthenticated ? <AuthenticatedEntryRedirect /> : children;
};

const PrivateRoute = ({ children }: { children: ReactElement }): ReactElement => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F4F5F7]">
        <TailSpin color="rgb(17,58,40)" height={80} width={80} />
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login/?mode=signup" replace />;
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route
          path="/login"
          element={
            <AdminRedirectRoute>
              <PublicAuthRoute><Auth /></PublicAuthRoute>
            </AdminRedirectRoute>
          }
        />
        <Route
          path="/pricing"
          element={
            <AdminRedirectRoute>
              <PricingRoute><Pricing /></PricingRoute>
            </AdminRedirectRoute>
          }
        />
        <Route
          path="/library"
          element={
            <AdminRedirectRoute>
              <SubscriptionRoute><Library /></SubscriptionRoute>
            </AdminRedirectRoute>
          }
        />
        <Route path="/live-classes" element={<AdminRoute><LiveClassesAdmin /></AdminRoute>} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/my-account" element={<PrivateRoute><MyAccount /></PrivateRoute>} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/imprint" element={<Imprint />} />
        <Route
          path="/checkout"
          element={
            <AdminRedirectRoute>
              <Checkout />
            </AdminRedirectRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
